import { modifier } from 'ember-modifier';
import type RouterService from '@ember/routing/router-service';

/**
 * Modifier that handles scroll behavior for mention hints.
 * 1. On page load with mentionId: scrolls to the mention
 * 2. On click: scrolls to mention and updates URL query param
 */
const scrollToMention = modifier(
  (element: Element, positional: unknown[], named: Record<string, unknown>) => {
    const router = named.router as RouterService | undefined;
    const mentionId = named.mentionId as string | undefined;
    const htmlElement = element as HTMLElement;

    // Auto-scroll on page load if mentionId is provided
    if (mentionId) {
      // Use requestAnimationFrame to wait for DOM to be ready
      requestAnimationFrame(() => {
        const mentionCard = document.getElementById(`mention-${mentionId}`);
        if (mentionCard) {
          mentionCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    const handleClick = (event: Event): void => {
      const target = event.target as HTMLElement;

      // Check if the clicked element (or its parent) is an item hint
      const hintElement = target.closest('.item-hint');
      if (!hintElement) return;

      const hintMentionId = hintElement.getAttribute('data-mention-id');
      if (!hintMentionId) return;

      // Prevent default link behavior
      event.preventDefault();
      event.stopPropagation();

      // Find and scroll to the mention card
      const mentionCard = document.getElementById(`mention-${hintMentionId}`);
      if (mentionCard) {
        mentionCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Update URL query param (triggers re-render with highlight)
      if (router) {
        router.transitionTo({ queryParams: { mentionId: hintMentionId } });
      }
    };

    htmlElement.addEventListener('click', handleClick, true);

    return () => {
      htmlElement.removeEventListener('click', handleClick, true);
    };
  }
);

export default scrollToMention;
